import React from "react";

/**
 * Line icons for the v2 apps (wzcab and wzconsole).
 *
 * Hand-rolled rather than pulled from an icon set: the cab screen is read at a glance in a truck,
 * so the strokes are deliberately heavier than a typical UI icon and the shapes stay legible at
 * 21px on a sunlit phone. They inherit currentColor so tab bars and buttons can tint them.
 */

type IconProps = { size?: number; className?: string };

const base = (size: number) => ({
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
});

/**
 * The Omnisight mark. Inherits currentColor so it sits on either theme.
 * Taken from the approved design; keep the path in step with the dashboard's.
 */
export const OmnisightMark: React.FC<IconProps> = ({ size = 30, className }) => (
    <svg width={size} height={size} viewBox="0 0 69 69" fill="currentColor" className={className} aria-hidden>
        <path d="M0.382521 29.8672C3.24396 23.2721 9.73047 13.0756 11.9124 10.6513Q16.1597 5.93208 21.9643 3.14774Q27.7689 0.363403 34.5646 0.363403Q41.6434 0.363403 47.4008 3.3365C51.2391 5.31857 54.5426 7.91414 57.3112 11.1232C59.4254 13.5738 65.7102 23.4556 68.5629 29.8672L57.0779 29.8672C56.8059 28.0371 56.3645 26.2409 55.7538 24.4786C54.6527 21.301 53.0796 18.5009 51.0346 16.0784Q47.9671 12.4446 43.767 10.2738Q39.5669 8.10293 34.4702 8.10292Q29.0903 8.10292 24.843 10.3681Q20.5957 12.6334 17.6226 16.3143Q14.6495 19.9953 13.045 24.7145Q12.1784 27.2633 11.7798 29.8672L0.382521 29.8672ZM34.5 27C30.3579 27 27 30.3579 27 34.5C27 38.6421 30.3579 42 34.5 42C38.6421 42 42 38.6421 42 34.5C42 30.3579 38.6421 27 34.5 27ZM13.0922 44.2049Q12.2384 41.7411 11.8259 39.1891L0.431854 39.1891C3.24981 45.5972 9.42474 55.3744 11.5349 57.7962C14.3035 60.9738 17.6226 63.5222 21.4924 65.4413C25.3621 67.3605 29.6566 68.32 34.3758 68.32C38.9063 68.32 43.1064 67.3919 46.9761 65.5357C50.8459 63.6795 54.1808 61.194 56.9808 58.0793C59.1288 55.6901 65.6425 45.7201 68.56 39.1891L57.0068 39.1891C56.7472 40.7586 56.3611 42.3204 55.8482 43.8745C54.81 47.0207 53.2998 49.8365 51.3178 52.3219C49.3357 54.8074 46.9289 56.8209 44.0974 58.3625C41.2658 59.9041 38.0568 60.6749 34.4702 60.6749C31.0094 60.6749 27.8633 59.9513 25.0318 58.5041C22.2002 57.0568 19.7777 55.1062 17.7642 52.6523Q14.7439 48.9713 13.0922 44.2049Z" />
    </svg>
);

export const CheckIcon: React.FC<IconProps> = ({ size = 14, className }) => (
    <svg {...base(size)} strokeWidth={3.2} className={className}>
        <path d="M5 12.5l4.6 4.5L19 7.5" />
    </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ size = 14, className }) => (
    <svg {...base(size)} strokeWidth={2.4} className={className}>
        <path d="M6 9l6 6 6-6" />
    </svg>
);

export const ChevronRightIcon: React.FC<IconProps> = ({ size = 16, className }) => (
    <svg {...base(size)} strokeWidth={2} className={className}>
        <path d="M9 6l6 6-6 6" />
    </svg>
);

export const AccountIcon: React.FC<IconProps> = ({ size = 17, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <circle cx="12" cy="8.4" r="3.6" />
        <path d="M4.8 19.4a7.4 7.4 0 0114.4 0" />
    </svg>
);

export const HomeIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M3.5 10.5L12 3.5l8.5 7" />
        <path d="M5.5 9.6V20h13V9.6" />
    </svg>
);

/** Concentric arcs — a signal radiating from the sensor, matching the radar metaphor. */
export const LiveIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <circle cx="12" cy="12" r="2.6" />
        <path d="M7.8 7.8a6 6 0 000 8.4M16.2 16.2a6 6 0 000-8.4" />
        <path d="M4.9 4.9a10 10 0 000 14.2M19.1 19.1a10 10 0 000-14.2" />
    </svg>
);

export const AlertsIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M12 3.5a5.5 5.5 0 015.5 5.5c0 5 2 6.5 2 6.5h-15s2-1.5 2-6.5A5.5 5.5 0 0112 3.5z" />
        <path d="M10 19a2 2 0 004 0" />
    </svg>
);

export const SettingsIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.7} className={className}>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M19.1 14.4a1.5 1.5 0 00.3 1.66l.05.05a1.82 1.82 0 11-2.58 2.58l-.05-.05a1.5 1.5 0 00-1.66-.3 1.5 1.5 0 00-.91 1.37v.15a1.82 1.82 0 11-3.64 0v-.08a1.5 1.5 0 00-.98-1.37 1.5 1.5 0 00-1.66.3l-.05.05a1.82 1.82 0 11-2.58-2.58l.05-.05a1.5 1.5 0 00.3-1.66 1.5 1.5 0 00-1.37-.91h-.15a1.82 1.82 0 110-3.64h.08a1.5 1.5 0 001.37-.98 1.5 1.5 0 00-.3-1.66l-.05-.05a1.82 1.82 0 112.58-2.58l.05.05a1.5 1.5 0 001.66.3h.07a1.5 1.5 0 00.91-1.37v-.15a1.82 1.82 0 113.64 0v.08a1.5 1.5 0 00.91 1.37 1.5 1.5 0 001.66-.3l.05-.05a1.82 1.82 0 112.58 2.58l-.05.05a1.5 1.5 0 00-.3 1.66v.07a1.5 1.5 0 001.37.91h.15a1.82 1.82 0 110 3.64h-.08a1.5 1.5 0 00-1.37.91z" />
    </svg>
);

/** Beacon / warning light — the relay-1 test. */
export const LightsIcon: React.FC<IconProps> = ({ size = 22, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M9 18h6M10 21h4" />
        <path d="M12 3a6 6 0 00-3.6 10.8c.5.4.8 1 .9 1.6l.1.6h5.2l.1-.6c.1-.6.4-1.2.9-1.6A6 6 0 0012 3z" />
    </svg>
);

/** Horn / speaker — the relay-2 test. */
export const HornIcon: React.FC<IconProps> = ({ size = 22, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M11 5L6 9H2v6h4l5 4V5z" />
        <path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" />
    </svg>
);

/** Speaker with the outer waves dropped — the horn, silenced. */
export const HornMutedIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M11 5L6 9H2v6h4l5 4V5z" />
        <path d="M16 9.5l5 5M21 9.5l-5 5" />
    </svg>
);

/** Four filled tiles — the dashboard/overview glyph. Filled, not stroked, so it reads as "grid". */
export const GridIcon: React.FC<IconProps> = ({ size = 22, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <rect x="3" y="3" width="8" height="8" rx="2" />
        <rect x="13" y="3" width="8" height="8" rx="2" />
        <rect x="3" y="13" width="8" height="8" rx="2" />
        <rect x="13" y="13" width="8" height="8" rx="2" />
    </svg>
);

/** Stacked rules — a log of events, not a notification bell. */
export const ListIcon: React.FC<IconProps> = ({ size = 22, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M4 6h16M4 12h16M4 18h10" />
    </svg>
);

export const UsersIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <circle cx="9" cy="8.5" r="3.2" />
        <path d="M3.2 19a5.8 5.8 0 0111.6 0" />
        <path d="M16.5 6.2a3.2 3.2 0 010 6M18 19a5.9 5.9 0 00-1.6-4" />
    </svg>
);

export const QrIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M4 8V5a1 1 0 011-1h3M16 4h3a1 1 0 011 1v3M20 16v3a1 1 0 01-1 1h-3M8 20H5a1 1 0 01-1-1v-3" />
        <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
);

export const DownloadIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M12 3v11M7.5 9.5L12 14l4.5-4.5M4 20h16" />
    </svg>
);

export const HelpIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.6 9.4a2.5 2.5 0 013.9-1.4c1.6 1 1 2.9-.5 3.6-.7.3-1 .9-1 1.6M12 17.2h.01" />
    </svg>
);

/** Camera / truck telematics glyph used for the devices nav and KPI tiles. */
export const TruckPanelIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <rect x="2.5" y="7" width="19" height="10" rx="2.5" />
        <path d="M6 12h.01M10 12h5" />
    </svg>
);

export const GaugeIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 12l4-2.5" />
    </svg>
);

export const TrendIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M4 18l5-6 4 3 7-9" />
    </svg>
);

export const SunIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2.5v2.6M12 18.9v2.6M2.5 12h2.6M18.9 12h2.6M5.4 5.4l1.9 1.9M16.7 16.7l1.9 1.9M18.6 5.4l-1.9 1.9M7.3 16.7l-1.9 1.9" />
    </svg>
);

/** Sun and moon together — the "follow the system" state, which is neither one nor the other. */
export const SunMoonIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M18.5 13.4A5.6 5.6 0 0111 5.9a6.4 6.4 0 107.5 7.5z" />
        <path d="M5.6 3.2v1.8M2 6.8h1.8M9.2 6.8H7.4M4.1 4.3l1.3 1.3M7.1 4.3L5.8 5.6" />
    </svg>
);

export const MoonIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
    </svg>
);

/** Three bars — opens the phone's navigation drawer. */
export const MenuIcon: React.FC<IconProps> = ({ size = 21, className }) => (
    <svg {...base(size)} strokeWidth={2} className={className}>
        <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
);

export const CloseIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={2.2} className={className}>
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

/** A funnel — the console's fleet scope (organization and group) where there is no room for the pickers. */
export const FilterIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M3.5 5h17l-6.6 7.6V19l-3.8-2v-4.4z" />
    </svg>
);
